import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  SKIP_GBE_ERROR_NOTIFICATION,
  SKIP_GBE_ERROR_RETRY
} from '@core/interceptors/gbe-error.interceptor';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import {
  OptimizationHistoryPage,
  OptimizationHistoryQuery
} from '@core/models/optimization-history.model';
import {
  OptimizationRequest,
  OptimizationResponse,
  OptimizationResult
} from '@core/models/optimization.model';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

function toOptimizationResult(response: OptimizationResponse): OptimizationResult {
  return {
    planId: response.plan.id,
    status: response.status,
    jobId: response.id,
    outputPlanId: response.outputPlan?.id,
    isCancelable: response.isCancelable
  };
}

@Injectable({
  providedIn: 'root'
})
export class OptimizationService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  public startOptimization(request: OptimizationRequest): Observable<boolean> {
    const url = `${this.buildUrl(GbeEndPointV4.OPTIMIZATIONS)}/run`;

    // The caller reports every failure itself, naming the missing `optimize` permission on a
    // 403, so the interceptor's generic notification would only duplicate it.
    const context = new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true);

    return new Observable<boolean>((observer) => {
      this.http.post<{ optimizationId: string }>(url, request, { context }).subscribe({
        next: () => {
          observer.next(true);
          observer.complete();
        },
        error: (err) => {
          console.error('Error in start of optimization', err);
          observer.error(err);
        }
      });
    });
  }

  /**
   * Stops a waiting or running optimization. Cancelling twice succeeds, so a double-click or
   * a retry after a timeout is safe. The 200 carries the refreshed optimization, mapped here
   * onto the shape the plan store already merges from the websocket.
   */
  public cancelOptimization$(optimizationId: string): Observable<OptimizationResult> {
    const url = `${this.buildUrl(GbeEndPointV4.OPTIMIZATIONS)}/${optimizationId}/cancel`;

    // The caller renders each refusal with its own copy — a 409 means two different things,
    // neither of which the interceptor's generic notification could tell apart.
    const context = new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true);

    return this.http
      .post<OptimizationResponse>(url, null, { context })
      .pipe(map(toOptimizationResult));
  }

  public getOptimization$(optimizationId: string): Observable<OptimizationResult> {
    const url = `${this.buildUrl(GbeEndPointV4.OPTIMIZATIONS)}/${optimizationId}`;

    // Only the assistant's silent catch-up after a reconnect reads a run this way, once per
    // tracked run, so a failure must neither toast nor retry.
    const context = new HttpContext()
      .set(SKIP_GBE_ERROR_NOTIFICATION, true)
      .set(SKIP_GBE_ERROR_RETRY, true);

    return this.http
      .get<OptimizationResponse>(url, { context })
      .pipe(map(toOptimizationResult));
  }

  /**
   * One page of the cross-plan history. The server answers 400 to cursor and order params, so
   * paging is by page/take only. Params are snake_case by hand: the request interceptor only
   * converts bodies.
   */
  public listHistory$(
    query: OptimizationHistoryQuery
  ): Observable<OptimizationHistoryPage> {
    let params = new HttpParams().set('page', query.page).set('take', query.take);
    for (const status of query.statuses) {
      params = params.append('status', status);
    }
    const optional: Record<string, string | number | undefined> = {
      data_space_id: query.dataSpaceId,
      business_unit_id: query.businessUnitId,
      launched_by_user_id: query.launchedByUserId,
      from_created_at: query.fromCreatedAt,
      to_created_at: query.toCreatedAt
    };
    for (const [key, value] of Object.entries(optional)) {
      if (value !== undefined) {
        params = params.set(key, value);
      }
    }
    params = params.set('include_deleted_plans', query.includeDeletedPlans);

    return this.http.get<OptimizationHistoryPage>(
      this.buildUrl(GbeEndPointV4.OPTIMIZATIONS),
      {
        params
      }
    );
  }

  /**
   * Retires a failed or canceled optimization, which drops it from its plan. The delete is
   * logical on the back end, and the 204 carries no body. Deleting one that is already gone
   * answers 404, so a double-click is reported rather than silently succeeding.
   */
  public deleteOptimization$(optimizationId: string): Observable<void> {
    const url = `${this.buildUrl(GbeEndPointV4.OPTIMIZATIONS)}/${optimizationId}`;

    // As with cancel, the caller names each refusal, so the interceptor's generic
    // notification would only duplicate it. Retries are off because the delete is not
    // idempotent: a committed delete answered with a gateway error would retry into the 404
    // its own success produced, reporting a failure the user cannot act on.
    const context = new HttpContext()
      .set(SKIP_GBE_ERROR_NOTIFICATION, true)
      .set(SKIP_GBE_ERROR_RETRY, true);

    return this.http.delete<void>(url, { context });
  }
}
